import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import api from '../services/api';
import { passkeyLogin } from '../services/passkey';

const AuthContext = createContext(null);

const DEFAULT_TERMS = {
  klant_singular: 'Klant',
  klant_plural: 'Klanten',
  klant_lidwoord: 'de'
};

export const AuthProvider = ({ children }) => {
  const [gebruiker, setGebruiker] = useState(null);
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [menuLabels, setMenuLabels] = useState({});
  const [organisatieModus, setOrganisatieModus] = useState(localStorage.getItem('organisatie_modus') || 'klant');
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Restore session + terminology cache uit localStorage
  useEffect(() => {
    const opgeslagenGebruiker = localStorage.getItem('gebruiker');
    if (opgeslagenGebruiker) {
      setGebruiker(JSON.parse(opgeslagenGebruiker));
    }
    const opgeslagenTerms = localStorage.getItem('terminology');
    if (opgeslagenTerms) {
      try { setTerms({ ...DEFAULT_TERMS, ...JSON.parse(opgeslagenTerms) }); } catch {}
    }
    const opgeslagenPerms = localStorage.getItem('permissions');
    if (opgeslagenPerms) {
      try { setPermissions(JSON.parse(opgeslagenPerms)); } catch {}
    }
    setLoading(false);
  }, []);

  // Fetch terminology vanaf server (na login of bij page-reload met geldige token)
  const fetchMenuLabels = useCallback(async () => {
    try {
      const r = await api.get('/sidebar/labels');
      setMenuLabels(r.data?.labels || {});
    } catch (e) { /* stil, default labels */ }
  }, []);

  const fetchTerminology = useCallback(async () => {
    try {
      const r = await api.get('/auth/bedrijf-info');
      const t = r.data?.bedrijf?.terminology;
      const _modus = r.data?.bedrijf?.organisatie_modus;
      if (_modus) { setOrganisatieModus(_modus); localStorage.setItem('organisatie_modus', _modus); }
      if (t && typeof t === 'object') {
        const merged = { ...DEFAULT_TERMS, ...t };
        setTerms(merged);
        localStorage.setItem('terminology', JSON.stringify(merged));
      }
    } catch (e) {
      // Stille fallback naar default — geen blokker
    }
  }, []);

  // Permissions ophalen vanaf server
  const fetchPermissions = useCallback(async () => {
    try {
      const r = await api.get('/rechten/mijn');
      const perms = r.data?.permissions || [];
      setPermissions(perms);
      localStorage.setItem('permissions', JSON.stringify(perms));
    } catch (e) {
      // Stille fallback — geen blokker bij netwerk/auth issue
    }
  }, []);

  // Bij gebruiker-set (login + refresh) → terminology + permissions ophalen
  useEffect(() => {
    if (gebruiker?.id && localStorage.getItem('token')) {
      fetchTerminology();
      fetchMenuLabels();
      fetchPermissions();
    }
  }, [gebruiker?.id]);

  const login = async (gebruikersnaam, wachtwoord) => {
    try {
      const response = await authAPI.login(gebruikersnaam, wachtwoord);
      const data = response.data;

      // 2FA-stap nodig?
      if (data.requires_mfa) {
        return {
          success: false,
          requiresMfa: true,
          mfaToken: data.mfa_token,
          message: 'Vul de code uit je Authenticator-app in'
        };
      }

      if (data.requires_mfa_setup) {
        return {
          success: false,
          requiresMfaSetup: true,
          setupToken: data.setup_token,
          message: data.message || 'Je organisatie vereist 2FA. Stel het nu in.'
        };
      }

      const { token, gebruiker } = data;
      localStorage.setItem('token', token);
      localStorage.setItem('gebruiker', JSON.stringify(gebruiker));
      setGebruiker(gebruiker);
      return { success: true, gebruiker };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Inloggen mislukt'
      };
    }
  };
  const forcedSetupStart = async (setupToken) => {
    try {
      const response = await api.post('/auth/2fa/forced-setup-start', { setup_token: setupToken });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Setup starten mislukt' };
    }
  };
  const forcedSetupComplete = async (setupToken, code) => {
    try {
      const response = await api.post('/auth/2fa/forced-setup-complete', { setup_token: setupToken, code });
      const { token, gebruiker, recovery_codes } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('gebruiker', JSON.stringify(gebruiker));
      return { success: true, gebruiker, recoveryCodes: recovery_codes };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Activeren mislukt' };
    }
  };
  const verifyMfaLogin = async (mfaToken, code) => {
    try {
      const response = await authAPI.loginMfa(mfaToken, code);
      const { token, gebruiker } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('gebruiker', JSON.stringify(gebruiker));
      setGebruiker(gebruiker);
      return { success: true, gebruiker };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Code-verificatie mislukt'
      };
    }
  };


  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('gebruiker');
    localStorage.removeItem('terminology');
    localStorage.removeItem('permissions');
    setGebruiker(null);
    setTerms(DEFAULT_TERMS);
    setPermissions([]);
  };

  const isIngelogd = () => !!gebruiker;
  const heeftRol = (...rollen) => gebruiker && rollen.includes(gebruiker.rol);
  const hasPermission = (code) => {
    if (gebruiker?.rol === 'super_admin') return true;
    return permissions.includes(code);
  };

  // Update terminology vanuit Instellingen UI
  const updateTerminology = async (newTerms) => {
    try {
      const r = await api.put('/auth/bedrijf-info/terminology', newTerms);
      if (r.data?.success) {
        const merged = { ...DEFAULT_TERMS, ...r.data.terminology };
        setTerms(merged);
        localStorage.setItem('terminology', JSON.stringify(merged));
        return { success: true, terminology: merged };
      }
      return { success: false, message: r.data?.message || 'Update mislukt' };
    } catch (e) {
      return { success: false, message: e.response?.data?.message || 'Server fout' };
    }
  };

  // Passkey-login (WebAuthn): zelfde sessie-opslag als login()
  const loginMetPasskey = async (gebruikersnaam) => {
    const data = await passkeyLogin(api, gebruikersnaam);
    if (!data?.token) return { success: false, message: data?.message || 'Passkey-login mislukt' };
    localStorage.setItem('token', data.token);
    localStorage.setItem('gebruiker', JSON.stringify(data.gebruiker));
    setGebruiker(data.gebruiker);
    try { await fetchPermissions?.(); } catch {}
    return { success: true, gebruiker: data.gebruiker };
  };

  return (
    <AuthContext.Provider value={{
      gebruiker, login, loginMetPasskey, verifyMfaLogin, forcedSetupStart, forcedSetupComplete, logout, isIngelogd, heeftRol, loading,
      terms, updateTerminology, fetchTerminology, organisatieModus, menuLabels, fetchMenuLabels,
      permissions, hasPermission, fetchPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth moet binnen AuthProvider worden gebruikt');
  }
  return context;
};

// Convenience hook voor als je alleen terms nodig hebt
export const useTerminology = () => {
  const { terms } = useAuth();
  return terms;
};

// Menu-label resolutie voor paginatitels e.d.: afdeling-label -> fallback
export const useMenuLabel = (key, fallbackLabel, fallbackIcon = '') => {
  const { menuLabels } = useAuth();
  const l = menuLabels?.[key] || {};
  return { label: l.label || fallbackLabel, icon: l.icon || fallbackIcon };
};
